<template>
    <div>
        <div class="row nopads mt-2">
            <div class="col nopads pl-1 item-detail-title">
                {{ $t('daily_summary.price_list_weight_combined') }}
            </div>
            <div class="col nopads pl-1 item-detail-text">
                {{ totalWeight.toFixed(2) }} {{ results.weight_unit }}
            </div>
        </div>
        <div class="row nopads mb-2">
            <div class="col nopads pl-1 item-detail-title">
                {{ $t('daily_summary.price_list_sum_combined', {currency: currency}) }}
            </div>
            <div class="col nopads pl-1 item-detail-text">
                {{ totalSum.toFixed(2) }} {{ currency }}
            </div>
        </div>

        <vue3-datatable
            v-if="tableItems && tableItems.length > 0"
            :rows="tableItems"
            :columns="fields"
            :pagination="false"
            :sticky-header="true"
            height="40vh"
            :sortable="true"
            skin="bh-table-compact"
            row-class="routa-table-row"
            cell-class="routa-table-cell"
        >
            <template #status="data">
              <span class="nopads" :class="{ transport: inDelivery(data.value.status), unloading: unloading(data.value.status) }">
                 {{ getStatusString(data.value.status) }}
              </span>
            </template>
        </vue3-datatable>
        <div
            v-if="loading"
            id="loader"
            class="spinner"
        />
    </div>
</template>

<script>
import {deliveryNoteHelper, units} from "@/components/mixins/DeliveryNoteMixin";
import {restApi} from "@/components/mixins/RestApiMixin";
import Vue3Datatable from '@bhplugin/vue3-datatable'
export default {
    name: "DeliveryNotePriceList.vue",
    components: {Vue3Datatable},
    mixins: [deliveryNoteHelper, units, restApi],
    props: {
        notes: {
            type: Array,
            default() {
                return []
            }
        },
        summaryItem: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            fields: [],
            tableItems: [],
            totalWeight: 0,
            totalSum: 0,
            results: {},
            loading: false
        }
    },
    computed: {
        currency() {
            return import.meta.env.VITE_APP_CURRENCY || '€'
        }
    },
    mounted() {
        this.fetchDeliveryPriceList()
    },
    methods: {
        initTableItems: function () {
            this.tableItems = []
            this.totalWeight = 0
            this.totalSum = 0
            let lastPrice = null
            const temp = []
            this.results && Object.keys(this.results).length !== 0 && this.notes && this.notes.length > 0 &&
            this.results.prices.forEach(item => {
                if (item.distance_to) {
                    let weight = this.getWeight(item.distance_from, item.distance_to, this.notes,
                        this.results.weight_unit, this.results.distance_unit)
                    let sum = 0
                    if (item.distance_to) {
                        sum = this.getSum(weight, item.price * this.results.multiplier/ 100)
                    }
                    this.totalWeight += weight
                    this.totalSum += sum
                    temp.push(
                        {
                            distance_from: item.distance_from,
                            distance_to: item.distance_to,
                            multiplier: (item.price * this.results.multiplier / 100).toFixed(3),
                            weight: weight.toFixed(2),
                            sum: sum.toFixed(2)
                        }
                    )
                    lastPrice = item
                } else if (lastPrice) {
                    let lastRowSum = 0
                    let lastRowWeight = 0
                    this.notes.forEach(note => {
                        let distance = 0
                        let sum = 0
                        if (note.trips) {
                            note.trips.forEach(trip => {
                                distance += trip.mode === 3 ? trip.length : 0
                            })
                        }
                        if (distance >= item.distance_from && note.unit === 't') {
                            sum += this.getSumWithLast(note.amount, distance, item.distance_from, item.price, this.results.multiplier, lastPrice )
                            lastRowSum += sum
                            lastRowWeight += note.amount
                            this.totalWeight += note.amount
                            this.totalSum += sum
                        }
                    })
                    temp.push(
                        {
                            distance_from: item.distance_from,
                            distance_to: item.distance_to,
                            multiplier: (item.price * this.results.multiplier / 100).toFixed(3),
                            weight: lastRowWeight.toFixed(2),
                            sum: lastRowSum.toFixed(2)
                        }
                    )
                }
            })
            this.$nextTick(() => this.tableItems = temp)
        },
        initFieldItems() {
            this.fields = [
                {
                    field: 'distance_from',
                    title: this.$t('daily_summary.price_list_from') + ' ' + '(' + this.results.distance_unit + ')',
                    sort: true,
                    headerClass: 'routa-table-header'
                },
                {
                    field: 'distance_to',
                    title: this.$t('daily_summary.price_list_to') + ' ' + '(' +  this.results.distance_unit + ')',
                    sort: true,
                    headerClass: 'routa-table-header'
                },
                {
                    field: 'multiplier',
                    title: this.$t('daily_summary.price_list_multiplier'),
                    sort: true,
                    headerClass: 'routa-table-header'
                },
                {
                    field: 'weight',
                    title: this.results.weight_unit && (this.results.weight_unit === units.TONS ? this.$t('daily_summary.price_list_tons') : this.$t('daily_summary.price_list_kilograms')) + ' (' + this.results.weight_unit + ')',
                    sort: true,
                    headerClass: 'routa-table-header'
                },
                {
                    field: 'sum',
                    title: this.$t('daily_summary.price_list_sum', {currency: import.meta.env.VITE_APP_CURRENCY}),
                    sort: true,
                    headerClass: 'routa-table-header'
                }
            ]
        },
        getWeight(distance_from, distance_to, notes, weight_unit, distance_unit) {
            let amount = 0
            notes && notes.length > 0 && notes.forEach(note => {
                let tripLengthSum = 0;
                note.trips && note.trips.length > 0 && note.trips.forEach(trip => {
                    if (trip.mode === 3) {
                        tripLengthSum += distance_unit === 'm' ? this.kilometersToMeters(trip.length) : trip.length
                    }
                })
                if (tripLengthSum > distance_from && tripLengthSum <= distance_to ||
                    tripLengthSum > distance_from && distance_to === undefined) {
                    if(note.unit === weight_unit) {
                        amount += note.amount
                    } else if(note.unit === units.TONS && weight_unit === units.KILOGRAMS) {
                        amount += this.tonsToKilograms(note.amount)
                    } else if(note.unit === units.KILOGRAMS && weight_unit === units.TONS) {
                        amount += this.kilogramsToTons(note.amount)
                    }
                }
            })
            return amount
        },
        getSum(weight, multiplier) {
            return multiplier * weight;
        },
        getSumWithLast(weight, distance, distanceFrom, price, multiplier, lastPrice) {
            let distanceDelta = distance - distanceFrom
            let basePriceValue = (weight * lastPrice.price * multiplier) / 100
            let addedPrice = (weight * price * multiplier * Math.ceil(distanceDelta)) / 100
            return basePriceValue + addedPrice
        },
        fetchDeliveryPriceList() {
            if (this.summaryItem) {
                this.loading = true
                this.restFetch(
                    this.orderUrl + "/" + this.summaryItem.order.id + "/pricelist",
                    this.fetchPriceListSuccess,
                    this.fetchFail
                )
            }
        },
        fetchPriceListSuccess: function (response) {
            this.results = response.data
            this.loading = false
            this.initTableItems()
            this.initFieldItems()
        },
        fetchFail: function () {
            this.loading = false
        }
    }
}
</script>
